import { ASK_PUNTT_COMMENT_CONTEXT_TITLE_MAX_CHARS } from "@mg/schemas/src/christo/askPuntt";
import { useCallback, useEffect, useRef } from "react";

import { reviewCommentTags } from "./reviewCommentTags";
import { type useAskPunttReview } from "./useAskPunttReview";
import { type useAskV2ReviewCommentFilter } from "./useAskV2ReviewCommentFilter";
import { type useAskV2ReviewWorkspace } from "./useAskV2ReviewWorkspace";

import { reviewCommentTitle } from "../../components/ask-puntt-review";
import { type AskV2CommentContext } from "../transport/types";
import { type AskV2TransportApi } from "../transport/useAskV2Transport";

type Review = NonNullable<
  NonNullable<ReturnType<typeof useAskPunttReview>["data"]>["review"]
>;

type UseAskV2ReviewCommentContextArgs = {
  review: Review | undefined;
  workspace: ReturnType<typeof useAskV2ReviewWorkspace>;
  transport: AskV2TransportApi;
  selectedCommentId: string | null;
  panelComments: readonly { _id: string }[];
  commentNumbers: Record<string, number>;
  isCommentFilterVisible: ReturnType<
    typeof useAskV2ReviewCommentFilter
  >["isVisible"];
  clearComment: () => void;
};

/**
 * A finding's place on the panel's list, from 1: the number its chip carries
 * when the pins give it none. The judge's rule request reads the same place, so
 * one finding never goes out under two numbers.
 */
export function panelNumberOf(
  panelComments: readonly { _id: string }[],
  commentId: string,
): number {
  return Math.max(
    panelComments.findIndex((item) => item._id === commentId) + 1,
    1,
  );
}

/**
 * Keeps the composer's comment chip in step with the finding selected in the
 * review: set while a visible finding is selected, cleared when it is not, and
 * cleared once the message carrying it is sent. The finding stays selected so
 * the comments list is still on it when the reader comes back from the chat.
 * Returns the chip for any finding of the review, which a rejection made an
 * enterprise rule sends with her reason.
 */
export function useAskV2ReviewCommentContext({
  review,
  workspace,
  transport,
  selectedCommentId,
  panelComments,
  commentNumbers,
  isCommentFilterVisible,
  clearComment,
}: UseAskV2ReviewCommentContextArgs) {
  const { setCommentContext, clearCommentContexts } = transport;
  // why: the sent turn drops the chip, but clearing the selection too opened the comments list at the top
  const releasedCommentIdRef = useRef<string | null>(null);
  const seenReleaseIdsRef = useRef(new Set<string>());

  useEffect(() => {
    if (!selectedCommentId || !review) return;
    const comment = review.comments.find(
      (item) => item._id === selectedCommentId,
    );
    const onPanel = panelComments.some((c) => c._id === selectedCommentId);
    if (!comment || !isCommentFilterVisible(comment) || !onPanel) {
      clearComment();
    }
  }, [
    clearComment,
    isCommentFilterVisible,
    panelComments,
    review,
    selectedCommentId,
  ]);

  // The chip a message about one finding carries: christo turns it into the
  // `review_id=... comment_id=...` line warhol reads. Built for any finding of
  // the review, shown on the panel or not: a rule request on a finding the
  // filter hides (a minor one rejected from the live list) still has to reach
  // the chat tied to it, because the ledger already says she asked for a rule.
  const commentContextFor = useCallback(
    (commentId: string): AskV2CommentContext | null => {
      if (!review) return null;
      const comment = review.comments.find((item) => item._id === commentId);
      if (!comment) return null;
      const title = reviewCommentTitle(comment);
      const normalizedTitle =
        title.length <= ASK_PUNTT_COMMENT_CONTEXT_TITLE_MAX_CHARS
          ? title
          : `${title.slice(0, ASK_PUNTT_COMMENT_CONTEXT_TITLE_MAX_CHARS - 3)}...`;
      return {
        id: `review-comment-${commentId}`,
        commentId,
        reviewId: review.reviewId,
        // why not 0: the send route requires a number of at least 1, and a finding the pins skip (a companion
        // row, or one the filter hides) has no pin number; its place on the panel stands in, not its place in
        // the whole review, which a filtered-out finding shifts (bobai on #6922)
        number:
          commentNumbers[commentId] ?? panelNumberOf(panelComments, commentId),
        title: normalizedTitle,
        tag: reviewCommentTags(comment)[0],
      };
    },
    [commentNumbers, panelComments, review],
  );

  useEffect(() => {
    let releasedThisRun = false;
    for (const message of transport.messages) {
      if (message.role !== "user" || message.status !== "sending") continue;
      if (seenReleaseIdsRef.current.has(message.clientId)) continue;
      seenReleaseIdsRef.current.add(message.clientId);
      // why the chip's own finding, not the selection alone: a rule request is
      // sent and the next finding selected in one tick, so the selection by
      // now names the finding she landed on, whose chip was never sent (bobai
      // on #6922)
      if (
        selectedCommentId &&
        message.commentContexts?.some(
          (context) => context.commentId === selectedCommentId,
        )
      ) {
        releasedCommentIdRef.current = selectedCommentId;
        releasedThisRun = true;
      }
    }
    if (!selectedCommentId || !review) {
      // why: the sent-finding marker must die with the selection, or selecting that same finding again never reattaches the chip
      if (!selectedCommentId) releasedCommentIdRef.current = null;
      clearCommentContexts();
      return;
    }
    // why: selectedCommentId is truthy here, so an already-null ref also differs and the null assignment is a no-op; no separate "!= null" guard (it would be an unkillable equivalent mutant)
    if (releasedCommentIdRef.current !== selectedCommentId) {
      releasedCommentIdRef.current = null;
    }
    if (releasedCommentIdRef.current === selectedCommentId) {
      if (releasedThisRun) clearCommentContexts();
      return;
    }
    const comment = review.comments.find(
      (item) => item._id === selectedCommentId,
    );
    const onPanel = panelComments.some((c) => c._id === selectedCommentId);
    const context =
      comment && isCommentFilterVisible(comment) && onPanel
        ? commentContextFor(selectedCommentId)
        : null;
    if (!context) {
      clearCommentContexts();
      return;
    }
    setCommentContext(context);
  }, [
    clearCommentContexts,
    commentContextFor,
    isCommentFilterVisible,
    panelComments,
    review,
    selectedCommentId,
    setCommentContext,
    transport.messages,
  ]);

  // why: the chip lives on the PAGE transport, which outlives this pane — the
  // composer the review column hosts is the same SummonBar the project feed
  // shows once the review closes. The effect above only clears while the
  // workspace is mounted, so closing the review with a finding selected left
  // that finding attached to the next message sent from the feed, and
  // `send()` fires even on an empty composer while a comment context is
  // attached. A ref, not a dep: a cleanup keyed on the transport callback
  // would re-run on every identity change and wipe the chip the effect above
  // had just set.
  const clearCommentContextsRef = useRef(clearCommentContexts);
  clearCommentContextsRef.current = clearCommentContexts;
  useEffect(() => () => clearCommentContextsRef.current(), []);

  const seenSendingUserIdsRef = useRef(new Set<string>());

  useEffect(() => {
    for (const message of transport.messages) {
      if (message.role !== "user" || message.status !== "sending") continue;
      if (seenSendingUserIdsRef.current.has(message.clientId)) continue;
      seenSendingUserIdsRef.current.add(message.clientId);
      if (workspace.leftPane === "comments") {
        workspace.setLeftPane("feed");
      }
    }
  }, [transport.messages, workspace.leftPane, workspace.setLeftPane]);

  return { commentContextFor };
}
